import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, ChevronDown, ChevronRight, Eye, Code, AlertTriangle, CheckCircle, Info, Database } from 'lucide-react';
import { Model } from '../../types';

interface ModelSchemaPreviewProps {
  model: Model;
  allModels?: Model[];
  onClose?: () => void;
  label?: string;
}

interface SchemaProperty {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  format?: string;
  enum?: string[];
  items?: any;
  properties?: Record<string, any>;
}

const ModelSchemaPreview: React.FC<ModelSchemaPreviewProps> = ({ model, onClose, label, allModels }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'schema' | 'relationships'>('schema');

  const handleViewRegistry = () => {
    navigate(`/projects/${model.project_id}/schemas?modelId=${model.model_id}&tab=registry&subtab=entries`);
    onClose?.();
  };

  const handleViewNestedRegistry = (nested: Model) => {
    navigate(`/projects/${nested.project_id}/schemas?modelId=${nested.model_id}&tab=registry&subtab=entries`);
    onClose?.();
  };

  // Parse the model properties to extract schema information
  const parsedSchema = useMemo(() => {
    try {
      const properties: SchemaProperty[] = [];
      const schema = typeof model.properties === 'string' 
        ? JSON.parse(model.properties) 
        : model.properties;

      if (schema && typeof schema === 'object') {
        // Check if properties are stored as simple strings (like "list[FinancialSearchItem]")
        // or as objects with type fields
        const isSimpleStringFormat = Object.values(schema).every(value => typeof value === 'string');
        
        if (isSimpleStringFormat) {
          // Handle simple string format where properties are stored as "fieldName": "typeString"
          Object.entries(schema).forEach(([key, value]) => {
            properties.push({
              name: key,
              type: value as string,
              required: true, // Assume required for simple string format
              description: undefined,
              format: undefined,
              enum: undefined,
              items: undefined,
              properties: undefined
            });
          });
        } else {
          // Handle object format where properties have type, required, etc. fields
          Object.entries(schema).forEach(([key, value]: [string, any]) => {
            properties.push({
              name: key,
              type: value.type || 'unknown',
              required: value.required || false,
              description: value.description,
              format: value.format,
              enum: value.enum,
              items: value.items,
              properties: value.properties
            });
          });
        }
      }

      return {
        properties,
        requiredFields: properties.filter(p => p.required),
        optionalFields: properties.filter(p => !p.required),
        totalFields: properties.length
      };
    } catch (error) {
      console.error('Error parsing schema:', error);
      return {
        properties: [],
        requiredFields: [],
        optionalFields: [],
        totalFields: 0
      };
    }
  }, [model.properties]);

  // Filter properties based on search query
  const filteredProperties = useMemo(() => {
    if (!searchQuery) return parsedSchema.properties;
    
    const query = searchQuery.toLowerCase();
    return parsedSchema.properties.filter(property =>
      property.name.toLowerCase().includes(query) ||
      property.type.toLowerCase().includes(query) ||
      property.description?.toLowerCase().includes(query)
    );
  }, [parsedSchema.properties, searchQuery]);

  const togglePropertyExpansion = (propertyName: string) => {
    setExpandedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyName)) {
        newSet.delete(propertyName);
      } else {
        newSet.add(propertyName);
      }
      return newSet;
    });
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      string: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      str: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      number: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      integer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      int: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      float: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      boolean: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      bool: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      array: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      list: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      object: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      dict: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      null: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      datetime: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
    };
    
    // Check if it's a list type
    if (type.startsWith('list[')) {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    }
    
    // Check if it's a literal type
    if (type.startsWith('Literal[')) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    }
    
    return colors[type.toLowerCase()] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const renderValidationRules = (): Array<{
    type: string;
    field: string;
    message: string;
    severity: 'error' | 'warning' | 'info';
  }> => {
    const validationRules: Array<{
      type: string;
      field: string;
      message: string;
      severity: 'error' | 'warning' | 'info';
    }> = [];
    
    parsedSchema.properties.forEach(property => {
      if (property.required) {
        validationRules.push({
          type: 'required',
          field: property.name,
          message: `${property.name} is required`,
          severity: 'error' as const
        });
      }
      
      if (property.format) {
        validationRules.push({
          type: 'format',
          field: property.name,
          message: `${property.name} must be in ${property.format} format`,
          severity: 'warning' as const
        });
      }
      
      if (property.enum) {
        validationRules.push({
          type: 'enum',
          field: property.name,
          message: `${property.name} must be one of: ${property.enum.join(', ')}`,
          severity: 'info' as const
        });
      }
    });

    return validationRules;
  };

  const validationRules = renderValidationRules();

  // Generate Pydantic class preview
  const pydanticPreview = useMemo(() => {
    const className = model.class_name || 'MyModel';
    let properties: Record<string, any> = {};
    try {
      properties = typeof model.properties === 'string' ? JSON.parse(model.properties) : model.properties;
    } catch {
      properties = {};
    }
    const typeMap: Record<string, string> = {
      string: 'str',
      number: 'float',
      integer: 'int',
      boolean: 'bool',
      array: 'List[Any]',
      object: 'dict',
      null: 'Optional[Any]'
    };
    const lines = [
      `class ${className}(BaseModel):`
    ];
    if (properties && Object.keys(properties).length > 0) {
      Object.entries(properties).forEach(([key, value]: [string, any]) => {
        let pyType;
        if (typeof value === 'string') {
          pyType = value;
        } else if (typeof value === 'object' && value !== null) {
          pyType = typeMap[value.type] || value.type || 'Any';
          if (value.required === false) {
            pyType = `Optional[${pyType}]`;
          }
        } else {
          pyType = 'Any';
        }
        lines.push(`    ${key}: ${pyType}`);
      });
    } else {
      lines.push('    pass');
    }
    return lines.join('\n');
  }, [model]);

  // Find referenced models for the Relationships tab
  const referencedModels = useMemo(() => {
    if (!allModels) return [];
    let properties: Record<string, any> = {};
    try {
      properties = typeof model.properties === 'string' ? JSON.parse(model.properties) : model.properties;
    } catch {
      properties = {};
    }
    const referenced: { name: string; classDef: string }[] = [];
    Object.entries(properties).forEach(([key, value]: [string, any]) => {
      let typeName = '';
      if (typeof value === 'string') {
        typeName = value;
      } else if (typeof value === 'object' && value !== null) {
        typeName = value.type;
      }
      // Extract inner type if typeName is a generic like list[Type] or Optional[Type]
      let match = typeName && typeName.match(/^(?:list|List|array|Array|Optional|Literal)\[(.+)\]$/);
      let possibleTypeNames = [typeName];
      if (match && match[1]) {
        possibleTypeNames.push(match[1]);
      }
      // Try to find a referenced model for any possible type name
      const refModel = allModels.find(m => possibleTypeNames.includes(m.class_name));
      if (refModel) {
        // Generate class preview for the referenced model
        let refProps: Record<string, any> = {};
        try {
          refProps = typeof refModel.properties === 'string' ? JSON.parse(refModel.properties) : refModel.properties;
        } catch {
          refProps = {};
        }
        const refLines = [`class ${refModel.class_name}(BaseModel):`];
        if (refProps && Object.keys(refProps).length > 0) {
          Object.entries(refProps).forEach(([k, v]: [string, any]) => {
            let pyType;
            if (typeof v === 'string') {
              pyType = v;
            } else if (typeof v === 'object' && v !== null) {
              pyType = v.type || 'Any';
              if (v.required === false) {
                pyType = `Optional[${pyType}]`;
              }
            } else {
              pyType = 'Any';
            }
            refLines.push(`    ${k}: ${pyType}`);
          });
        } else {
          refLines.push('    pass');
        }
        referenced.push({ name: refModel.class_name, classDef: refLines.join('\n') });
      }
    });
    return referenced;
  }, [model, allModels]);

  // AC-1588: Surface View Registry Entries for nested registry-backed models
  // (e.g. a wrapper `DomainNames { domain_names: list[DomainName] }` should
  // expose access to the DomainName registry even though the wrapper itself
  // is not registry-enabled).
  const nestedRegistryModels = useMemo(() => {
    if (!allModels) return [] as Model[];
    let properties: Record<string, any> = {};
    try {
      properties = typeof model.properties === 'string' ? JSON.parse(model.properties) : model.properties;
    } catch {
      properties = {};
    }
    const found: Model[] = [];
    Object.values(properties || {}).forEach((value: any) => {
      let typeName = '';
      if (typeof value === 'string') {
        typeName = value;
      } else if (typeof value === 'object' && value !== null) {
        typeName = value.type;
      }
      const match = typeName && typeName.match(/^(?:list|List|array|Array|Optional|Literal)\[(.+)\]$/);
      const possibleTypeNames = [typeName, match?.[1]].filter(Boolean) as string[];
      const refModel = allModels.find(m => possibleTypeNames.includes(m.class_name));
      if (
        refModel?.registry_enabled &&
        refModel.model_id !== model.model_id &&
        !found.some(f => f.model_id === refModel.model_id)
      ) {
        found.push(refModel);
      }
    });
    return found;
  }, [model, allModels]);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">{model.class_name}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{model.description || 'No description provided'}</p>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          {model.registry_enabled && (
            <button
              type="button"
              onClick={handleViewRegistry}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
            >
              <Database className="w-4 h-4 mr-1.5" />
              View Registry Entries
            </button>
          )}
          {nestedRegistryModels.map(nested => (
            <button
              key={nested.model_id}
              type="button"
              onClick={() => handleViewNestedRegistry(nested)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
              title={`Open registry entries for ${nested.class_name}`}
            >
              <Database className="w-4 h-4 mr-1.5" />
              View {nested.class_name} Entries
            </button>
          ))}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400 mr-1">Total Fields:</span>
            <span className="font-medium text-gray-900 dark:text-warm-white">{parsedSchema.totalFields}</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400 mr-1">Required:</span>
            <span className="font-medium text-gray-900 dark:text-warm-white">{parsedSchema.requiredFields.length}</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400 mr-1">Optional:</span>
            <span className="font-medium text-gray-900 dark:text-warm-white">{parsedSchema.optionalFields.length}</span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab('schema')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'schema'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
          }`}
        >
          Model Structure
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('relationships')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'relationships'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
          }`}
        >
          Relationships
        </button>
      </div>
      {/* Tab Content */}
      {activeTab === 'schema' && (
        <div className="p-4">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
            <pre className="text-sm">
              {String(pydanticPreview)}
            </pre>
          </div>
        </div>
      )}
      {activeTab === 'relationships' && (
        <div className="p-4">
          {referencedModels.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No relationships found.</div>
          ) : (
            <div>
              <h4 className="text-md font-semibold mb-4">Referenced Models</h4>
              {referencedModels.map(ref => (
                <div key={ref.name} className="mb-6">
                  <div className="mb-2 text-sm text-gray-700 dark:text-gray-300">Referenced: <span className="font-mono">{ref.name}</span></div>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm">{String(ref.classDef)}</pre>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Property Card Component
interface PropertyCardProps {
  property: SchemaProperty;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  getTypeColor: (type: string) => string;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  isExpanded,
  onToggleExpansion,
  getTypeColor
}) => {
  const hasNestedData = property.properties || property.items;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {hasNestedData && (
            <button
              onClick={onToggleExpansion}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 dark:text-warm-white">{property.name}</span>
              {property.required && (
                <span className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded">
                  Required
                </span>
              )}
            </div>
            {property.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{property.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(property.type)}`}>
            {property.type}
          </span>
          {property.format && (
            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
              {property.format}
            </span>
          )}
        </div>
      </div>

      {property.enum && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Allowed values:</div>
          <div className="flex flex-wrap gap-1">
            {property.enum.map((value, index) => (
              <span
                key={index}
                className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded"
              >
                {value}
              </span>
            ))}
          </div>
        </div>
      )}

      {isExpanded && hasNestedData && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="bg-gray-50 dark:bg-gray-900 rounded p-3">
            <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
              {JSON.stringify(property.properties || property.items, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Validation Rule Card Component
interface ValidationRuleCardProps {
  rule: {
    type: string;
    field: string;
    message: string;
    severity: 'error' | 'warning' | 'info';
  };
}

const ValidationRuleCard: React.FC<ValidationRuleCardProps> = ({ rule }) => {
  const getSeverityIcon = () => {
    switch (rule.severity) {
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'info':
        return <Info className="w-4 h-4 text-primary-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getSeverityColor = () => {
    switch (rule.severity) {
      case 'error':
        return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
      case 'warning':
        return 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20';
      case 'info':
        return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20';
      default:
        return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getSeverityColor()}`}>
      <div className="flex items-start space-x-3">
        {getSeverityIcon()}
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900 dark:text-warm-white">{rule.field}</span>
            <span className="text-xs bg-white dark:bg-cap-dark px-2 py-0.5 rounded border">
              {rule.type}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{rule.message}</p>
        </div>
      </div>
    </div>
  );
};

export default ModelSchemaPreview;